import { nanoid } from 'nanoid';
import Yup from 'utils/validation/yup-extensions';
import { ValidateYupNPI } from '../1617BridgesLink/components/NPINumberField';
import {
	VALIDATION_SCHEMA as RESEARCH_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as RESEARCH_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/ResearchSupplement';

/**
 * T_Component FI_ID for the "form" component
 */
export const EPIC_APP_ID = 1616;

/**
 * T_Component FI_ID for the "form" component
 */
export const EPIC_COMPONENT_ID = 5532;

export const MAX_COUNTS = {
	JOB_CATEGORIES: 7,
};

/**
 * Keys to be used by Formik
 */
export const EPIC_FIELD_KEYS = {
	COMMENTS: 'comments',
	DEFAULT_LOGIN_DEPARTMENT: 'defaultLoginDepartment',
	JOB_CATEGORIES: 'jobCategories',
	HAS_SUPPLEMENTS: 'hasSupplements',
	NPI: 'npi',
	NEED_NPI: 'needNpi',
	NPI_VERIFICATION: 'npiVerification',
	NEED_RESEARCH: 'needResearch',
	RESEARCH_CITI_TRAINING: 'RESEARCH_CITI_TRAINING',
	RESEARCH_CITI_TRAINING_DATE: 'RESEARCH_CITI_TRAINING_DATE',
	RESEARCH_STUDY_TYPE: 'RESEARCH_STUDY_TYPE',
	RESEARCH_STUDY_ID: 'RESEARCH_STUDY_ID',
	SECTION_MAIN: 'sectionMain',
	TRACKING_NUMBER: 'TRACKING_NUMBER',
};

/**
 * Form field metadata (default values, validation schema)
 */
export const EPIC_FIELDS = {
	[EPIC_FIELD_KEYS.COMMENTS]: {
		defaultValue: '',
		validationSchema: Yup.string().max(
			100,
			'You have exceeded the character count (100).',
		),
	},
	[EPIC_FIELD_KEYS.DEFAULT_LOGIN_DEPARTMENT]: {
		defaultValue: '',
		validationSchema: Yup.object()
			.shape({
				departmentId: Yup.number(),
				departmentName: Yup.string(),
			})
			.default(null)
			.nullable()
			.required('Please select a default department.'),
	},
	[EPIC_FIELD_KEYS.JOB_CATEGORIES]: {
		defaultValue: [
			{
				jobCategoryId: nanoid(),
				jobCategoryName: null,
				jobCategoryType: null,
				jobCategoryExperience: 'New',
				active: true,
				wave: null,
			},
		],
		validationSchema: Yup.array()
			.of(
				Yup.object()
					.shape({
						jobCategoryId: Yup.mixed(),
						jobCategoryName: Yup.string()
							.test(
								'active-job-category-name',
								'Please select a job category.',
								function (value) {
									if (this.parent?.active === false) {
										return true;
									}

									return !!String(value ?? '').trim();
								},
							)
							.nullable(),
						jobCategoryType: Yup.string().nullable(),
						jobCategoryExperience: Yup.string()
							.test(
								'active-job-category-experience',
								'Please specify experience',
								function (value) {
									if (this.parent?.active === false) {
										return true;
									}

									return !!String(value ?? '').trim();
								},
							)
							.nullable(),
						wave: Yup.number()
							.test(
								'active-job-category-wave',
								'Please specify a wave',
								function (value) {
									if (this.parent?.active === false) {
										return true;
									}

									return (
										value !== null && value !== undefined
									);
								},
							)
							.nullable(),
					})
					.default(null)
					.nullable(),
			)
			.min(1, 'You must have at least one job category.')
			.max(
				MAX_COUNTS.JOB_CATEGORIES,
				'You can select no more than ' +
					MAX_COUNTS.JOB_CATEGORIES +
					' job categories',
			)
			.test('enhancement count', async function (value) {
				if (value && value.length > 0) {
					const activeJobCategories = value.filter(
						jc => jc?.active !== false,
					);

					const primaryJCs = activeJobCategories.filter(
						jc =>
							jc?.jobCategoryType?.toLowerCase() ===
							'primary category',
					);
					const enhancementJCs = activeJobCategories.filter(
						jc =>
							jc?.jobCategoryType?.toLowerCase() ===
							'enhancement category',
					);
					if (enhancementJCs.length > 0 && primaryJCs.length === 0) {
						return this.createError({
							message:
								'You must have at least one primary job category',
						});
					}
				}
				return true;
			})
			.test('template builder check', async function (value) {
				if (value && value.length > 0) {
					const defaultLoginDepartment =
						this.parent[EPIC_FIELD_KEYS.DEFAULT_LOGIN_DEPARTMENT];
					if (
						!defaultLoginDepartment ||
						defaultLoginDepartment?.isAffiliate
					) {
						return true;
					}

					const nonAffiliatedJC = value
						.filter(jc => jc?.active !== false)
						.find(jc => shouldFilterJobCategory(jc));
					if (nonAffiliatedJC) {
						return this.createError({
							message: `The default login department selected is not an affiliated department and therefore '${nonAffiliatedJC.jobCategoryName}' cannot be selected.`,
						});
					}
				}
				return true;
			}),
	},
	[EPIC_FIELD_KEYS.NEED_NPI]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.NPI]: {
		defaultValue: '',
		validationSchema: Yup.string()
			.maxCounter(10)
			.test('isNpiValidWhenRequired', async function (value) {
				if (!this.parent[EPIC_FIELD_KEYS.NEED_NPI]) {
					return true;
				}

				return await ValidateYupNPI(value, this);
			}),
	},
	[EPIC_FIELD_KEYS.NPI_VERIFICATION]: {
		defaultValue: false,
		validationSchema: Yup.bool().when(EPIC_FIELD_KEYS.NEED_NPI, {
			is: true,
			then: schema =>
				schema
					.oneOf([true], 'Please acknowledge.')
					.required('Please acknowledge.'),
		}),
	},
	[EPIC_FIELD_KEYS.TRACKING_NUMBER]: {
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[EPIC_FIELD_KEYS.NEED_RESEARCH]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.RESEARCH_CITI_TRAINING]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_CITI_TRAINING
				],
		}),
	},
	[EPIC_FIELD_KEYS.RESEARCH_CITI_TRAINING_DATE]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_CITI_TRAINING_DATE
				],
		}),
	},
	[EPIC_FIELD_KEYS.RESEARCH_STUDY_TYPE]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_STUDY_TYPE
				],
		}),
	},
	[EPIC_FIELD_KEYS.RESEARCH_STUDY_ID]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_STUDY_ID
				],
		}),
	},
};

export const TEMPLATE_BUILDER_ACCESS_RESTRICTIONS = {
	id: 1071,
	name: 'Template Builder - Scheduling',
};

export const templateBuilderFilter = (jc, isAffiliateDepartment) => {
	return isAffiliateDepartment || !shouldFilterJobCategory(jc);
};

export const shouldFilterJobCategory = jc => {
	return (
		jc.jobCategoryId === TEMPLATE_BUILDER_ACCESS_RESTRICTIONS.id ||
		jc.jobCategoryName?.toLocaleLowerCase() ===
			TEMPLATE_BUILDER_ACCESS_RESTRICTIONS.name.toLocaleLowerCase()
	);
};

export const NO_ACCESS_OR_TRAINING_NEEDED_JOB_CATEGORIES = [
	{
		id: 1426,
		name: 'No Access or Training Needed',
	},
	{
		id: 100000,
		name: 'No Access or Training Needed',
	},
];
